import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// Attach stored token to every request — checks both localStorage and sessionStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('govirtual_token') || sessionStorage.getItem('govirtual_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Public ────────────────────────────────────────────────────────────────────

// Fetch a published Form by org slug + form slug (used by the form renderer)
export const fetchPublicForm = (orgSlug, formSlug) =>
  api.get(`/forms/public/${orgSlug}/${formSlug}`);

// Submit a form (multipart/form-data to support file uploads)
export const submitForm = (formData) =>
  api.post('/submissions', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// Resolve any public URL path → returns { type, data, organization, ... }
// orgSlug is resolved server-side from PUBLIC_ORG_SLUG env var
export const resolvePath = (path) =>
  api.get(`/resolve/${path}`);

// Direct fetches (still used internally by EventPage and FormPage)
export const fetchPublicEvent = (orgSlug, eventSlug) =>
  api.get(`/events/public/${orgSlug}/${eventSlug}`);

// ─── Lobby ─────────────────────────────────────────────────────────────────────
export const fetchPublicLobby        = (eventId)        => api.get(`/lobbies/public/${eventId}`);
export const fetchPublicLobbyBySlug  = (eventId, slug)  => api.get(`/lobbies/public/${eventId}/${slug}`);

// ─── Admin Auth ────────────────────────────────────────────────────────────────

export const loginAdmin = (data) => api.post('/auth/admin/login', data);
export const registerAdmin = (data) => api.post('/auth/admin/register', data);

// ─── Participant Auth ──────────────────────────────────────────────────────────

export const loginParticipant    = (data) => api.post('/auth/participant/login', data);
export const registerParticipant = (data) => api.post('/auth/participant/register', data);
export const verifyParticipant   = ()     => api.get('/auth/participant/verify');
export const logoutParticipant   = ()     => api.post('/auth/participant/logout');

// ─── Admin: Events ─────────────────────────────────────────────────────────────

export const fetchEvents = () => api.get('/events');
export const fetchEvent = (id) => api.get(`/events/${id}`);
export const createEvent = (data) => api.post('/events', data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data);
export const deleteEvent = (id) => api.delete(`/events/${id}`);

// ─── Admin: Forms ──────────────────────────────────────────────────────────────

export const fetchForms = (params) => api.get('/forms', { params });
export const fetchForm = (id) => api.get(`/forms/${id}`);
export const createForm = (data) => api.post('/forms', data);
export const updateForm = (id, data) => api.put(`/forms/${id}`, data);
export const updateFormStatus = (id, status) => api.patch(`/forms/${id}/status`, { status });
export const deleteForm = (id) => api.delete(`/forms/${id}`);

// ─── Admin: Submissions ────────────────────────────────────────────────────────

export const fetchSubmissions = (params) => api.get('/submissions', { params });
export const fetchSubmission = (id) => api.get(`/submissions/${id}`);
export const updateSubmissionStatus = (id, status, reviewNote) =>
  api.patch(`/submissions/${id}/status`, { status, reviewNote });
export const deleteSubmission = (id) => api.delete(`/submissions/${id}`);

export default api;
